import { inject, Injectable } from '@angular/core';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { ABSENCE_TYPE_TRANSLATION_KEYS } from '@app/shared/types/enums/absence-type';
import { Company } from '@app/shared/types/models/company/company.model';
import { AbsenceReport, isAbsenceReport } from '@app/shared/types/models/reports/absence-report';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { TimeReportRowVM } from '@app/shared/types/models/reports/time-report-row.model';
import { isWorkTimeReport, WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { User } from '@app/shared/types/models/user/user.model';
import { timeReportRowTypeKey } from '@app/shared/utils/time-report-table.utils';

/**
 * TimeReportRowMapper - Maps domain time reports to a presentation row for mixed Reports.
 *
 * Resolves WorkTime company via task/project in existing data stores (no HTTP).
 * Does not translate type labels; stores i18n keys only.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Reports flat table maps mixed TimeReport[] to {@link TimeReportRowVM}.
 */
@Injectable({ providedIn: 'root' })
export class TimeReportRowMapper {
  private readonly projectsDataService = inject(ProjectsDataService);
  private readonly tasksDataService = inject(TasksDataService);

  public map(report: TimeReport): TimeReportRowVM {
    if (isWorkTimeReport(report)) {
      return this.mapWorkTime(report);
    }
    if (isAbsenceReport(report)) {
      return this.mapAbsence(report);
    }
    throw new Error(`Unrecognized time report type: ${String(report.reportType)}`);
  }

  public mapMany(reports: TimeReport[]): TimeReportRowVM[] {
    return reports.map(item => this.map(item));
  }

  private mapWorkTime(report: WorkTimeReport): TimeReportRowVM {
    const user = this.readUser(report);
    const createdBy = this.readCreatedBy(report);
    const company = this.resolveWorkTimeCompany(report);
    const hoursType = report.hoursType;

    return {
      kind: 'WorkTime',
      id: report.id,
      date: report.date,
      userId: user?.id,
      userName: this.formatUserName(user),
      createdById: createdBy?.id,
      createdByName: this.formatUserName(createdBy),
      companyId: company?.id,
      companyName: company?.name,
      projectId: report.projectId ?? report.project?.id,
      projectName: report.project?.name,
      task: report.task
        ? {
            id: report.task.id,
            name: report.task.name,
            taskNumber: report.task.taskNumber,
            taskTypeCode: report.task.taskType?.code
          }
        : undefined,
      locationId: report.location?.id ?? report.locationId,
      locationName: report.location?.name,
      typeKey: timeReportRowTypeKey('WorkTime', hoursType),
      typeTranslationKey: hoursType ? `hours-type-abbr-${hoursType}` : undefined,
      description: report.description,
      comment: this.normalizeComment(report.description),
      duration: report.duration,
      status: report.status,
      estimation: report.task?.estimation?.time,
      source: report
    };
  }

  private mapAbsence(report: AbsenceReport): TimeReportRowVM {
    const user = this.readUser(report);
    const createdBy = this.readCreatedBy(report);
    const company = report.company?.();
    const absenceType = report.absenceType;

    return {
      kind: 'Absence',
      id: report.id,
      date: report.date,
      userId: user?.id,
      userName: this.formatUserName(user),
      createdById: createdBy?.id,
      createdByName: this.formatUserName(createdBy),
      companyId: company?.id,
      companyName: company?.name,
      projectId: undefined,
      projectName: undefined,
      task: undefined,
      locationId: undefined,
      locationName: undefined,
      typeKey: timeReportRowTypeKey('Absence', absenceType),
      typeTranslationKey: absenceType ? ABSENCE_TYPE_TRANSLATION_KEYS[absenceType] : undefined,
      description: report.description,
      comment: this.normalizeComment(report.comments),
      duration: report.duration,
      status: report.status,
      estimation: undefined,
      source: report
    };
  }

  private resolveWorkTimeCompany(report: WorkTimeReport): Company | undefined {
    const task = report.taskId ? this.tasksDataService.getById(report.taskId)() : undefined;
    const companyFromTask = task?.project?.()?.company?.();
    if (companyFromTask) {
      return companyFromTask;
    }

    const projectId = report.projectId ?? report.project?.id ?? task?.project?.()?.id;
    if (!projectId) {
      return undefined;
    }

    return this.projectsDataService.getById(projectId)()?.company?.();
  }

  private readUser(report: TimeReport): User | undefined {
    return report.user?.();
  }

  private readCreatedBy(report: TimeReport): User | undefined {
    return report.createdBy?.();
  }

  private formatUserName(user: User | undefined): string {
    if (!user) {
      return '';
    }
    return user.fullName?.trim() ?? '';
  }

  private normalizeComment(comment: string | undefined): string | undefined {
    return comment?.trim() || undefined;
  }
}
